/**
 * Vanilla replacement for the legacy `public/js/clepta.js` (jQuery-based).
 * Imported once from `app.tsx`. Wires up the chrome-level interactions that
 * live outside the Solid tree:
 *   - removes the body-level preloader
 *   - shows / scrolls the floating "scroll to top" button
 *   - jumps to in-page anchor links with a `.related` class
 *   - jumps to a `#hash` target on page load (below the fixed header)
 *   - opens / closes the mobile off-canvas menu
 *   - coerces installed PWAs into real fullscreen
 *   - wires the navbar fullscreen toggle
 */

import {
    exitDocumentFullscreen,
    fullscreenSupported,
    isAnyFullscreen,
    isApiFullscreen,
    isInstalledPwa,
    isSmallPhone,
    requestDocumentFullscreen,
} from './utils/device';
import { getScrollTop, scrollToElement, scrollToTop } from './utils/scroll';

const onReady = (cb: () => void): void => {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
        cb();
    }
};

function deactivatePreloader(): void {
    document.body.classList.add('preloader-deactive');
}

function setupScrollToTop(): void {
    const btn = document.querySelector<HTMLElement>('.scroll-to-top');
    if (!btn) return;

    const sync = () => {
        const show = getScrollTop() > 250;
        btn.style.display = show ? '' : 'none';
        btn.style.opacity = show ? '1' : '0';
    };
    sync();
    // Listen on both the window and document (capture phase) so we catch
    // scroll regardless of which element is actually the scroll root.
    window.addEventListener('scroll', sync, { passive: true });
    document.addEventListener('scroll', sync, { passive: true, capture: true });

    btn.addEventListener('click', (e) => {
        e.preventDefault();
        scrollToTop();
    });
}

function setupRelatedAnchorScroll(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const hash = target.closest<HTMLAnchorElement>('a.related')?.hash;
        if (!hash) return;
        const dest = document.querySelector(hash);
        if (!dest) return;
        event.preventDefault();
        scrollToElement(dest);
    });
}

function jumpToInitialHash(): void {
    const hash = window.location.hash;
    if (!hash) return;
    try {
        const dest = document.querySelector(hash);
        if (dest) scrollToElement(dest);
    } catch {
        /* invalid selector — ignore */
    }
}

function setupOffCanvasMenu(): void {
    const wrapper = document.querySelector<HTMLElement>('.off-canvas-wrapper');
    if (!wrapper) return;

    const open = () => {
        wrapper.classList.add('active');
        document.body.classList.add('fix');
    };
    const close = () => {
        wrapper.classList.remove('active');
        document.body.classList.remove('fix');
    };

    document.querySelectorAll('.btn-menu').forEach((btn) => btn.addEventListener('click', open));
    wrapper
        .querySelectorAll('.close-action > .btn-close, .off-canvas-overlay')
        .forEach((el) => el.addEventListener('click', close));

    // Capture phase so we close the menu *before* Solid Router's bubble-phase
    // listener takes over and starts the in-app navigation.
    document.addEventListener(
        'click',
        (event) => {
            const t = event.target;
            if (t instanceof Element && t.closest('.asside-menu a, .asside-menu li')) close();
        },
        true,
    );
}

/**
 * Force fullscreen inside the installed PWA. The manifest deliberately asks
 * for `display: standalone`: Android WebAPKs cache their display mode and
 * stay `standalone` anyway, and a manifest that keeps disagreeing with the
 * installed WebAPK makes Chrome re-mint (= reinstall) it on every launch,
 * which Android announces with an "icon changed" notice. So we get the
 * immersive look here instead: enter real fullscreen on the first user
 * gesture (the Fullscreen API requires one), hiding the status/nav bars.
 * No-op in a normal browser tab, or when something already went fullscreen.
 */
function setupPwaFullscreen(): void {
    if (!isInstalledPwa() || !fullscreenSupported()) return;
    // Already immersive — e.g. the launcher or a previous session did it.
    if (isAnyFullscreen()) return;

    // Keep listening across gestures until fullscreen actually engages. Android
    // Chrome is inconsistent about which gesture types it accepts for a
    // fullscreen request, so we retry on each rather than spending our one shot
    // on the first (e.g. pointerdown) and giving up if it's rejected.
    const gestures = ['pointerup', 'touchend', 'click', 'keydown'] as const;

    const cleanup = () => {
        gestures.forEach((evt) => document.removeEventListener(evt, tryEnter, true));
        document.removeEventListener('fullscreenchange', onChange);
    };
    const onChange = () => {
        if (isAnyFullscreen()) cleanup();
    };
    function tryEnter() {
        if (isAnyFullscreen()) {
            cleanup();
            return;
        }
        // Engaged -> stop listening. Refused -> keep the listeners so the
        // next gesture tries again.
        requestDocumentFullscreen().then(onChange, () => {});
    }

    gestures.forEach((evt) => document.addEventListener(evt, tryEnter, true));
    document.addEventListener('fullscreenchange', onChange);
}

/**
 * Wire the navbar "volledig scherm" toggle. It requests fullscreen on
 * `documentElement` — the same target used by the fullscreen hint — which is what
 * lets the lightbox stay immersive when a photo is closed: PhotoSwipe only
 * exits fullscreen on close when *it* owns the fullscreen (on pswpContainer),
 * so a document-level fullscreen survives the close morph untouched (see the
 * `pswp.close` override in photoswipe.js).
 *
 * Shown on desktop + tablet only, where document fullscreen actually works
 * and we're not an already-immersive installed PWA (same rules as the
 * fullscreen hint). The button is hidden in markup and revealed here.
 */
function setupFullscreenToggle(): void {
    const btn = document.querySelector<HTMLButtonElement>('.js-fullscreen-toggle');
    if (!btn) return;

    if (!fullscreenSupported() || isInstalledPwa() || isSmallPhone()) {
        btn.remove();
        return;
    }

    const requestIcon = btn.querySelector<SVGElement>('.js-fs-icn-request');
    const exitIcon = btn.querySelector<SVGElement>('.js-fs-icn-exit');
    const syncState = () => {
        const apiFs = isApiFullscreen();
        // Fullscreen we didn't request (F11 / OS) can't be left through any
        // API, so a toggle button would be dead — hide it in that state
        // instead. It reappears once F11 is released, because the display-mode
        // media query change re-runs syncState.
        if (!apiFs && isAnyFullscreen()) {
            btn.style.display = 'none';
            return;
        }
        btn.style.display = '';
        const label = apiFs ? 'Volledig scherm sluiten' : 'Volledig scherm';
        btn.setAttribute('title', label);
        btn.setAttribute('aria-label', label);
        if (requestIcon) requestIcon.style.display = apiFs ? 'none' : '';
        if (exitIcon) exitIcon.style.display = apiFs ? '' : 'none';
    };
    syncState();

    btn.addEventListener('click', () => {
        // The button is only visible when either not fullscreen or in our own
        // API fullscreen (F11 hides it), so these two cases cover every click.
        if (isApiFullscreen()) exitDocumentFullscreen();
        else requestDocumentFullscreen().catch(() => {});
    });

    document.addEventListener('fullscreenchange', syncState);
    document.addEventListener('webkitfullscreenchange', syncState);
    // F11 / OS fullscreen only surfaces through the display-mode media query.
    try {
        window.matchMedia('(display-mode: fullscreen)').addEventListener('change', syncState);
    } catch {
        /* matchMedia unsupported — the icon just won't track F11 */
    }
}

export function initCleptaRuntime(): void {
    onReady(() => {
        deactivatePreloader();
        setupScrollToTop();
        setupRelatedAnchorScroll();
        setupOffCanvasMenu();
        jumpToInitialHash();
        setupPwaFullscreen();
        setupFullscreenToggle();
    });

    // Window load also runs the preloader removal in case some image is
    // still in flight when the runtime mounts.
    window.addEventListener('load', deactivatePreloader, { once: true });
}
